<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/AppIcon.vue'
import { useWarungStore } from '@/stores/warung'
import type { AppIconName } from '@/types/icons'
import type { WarungPermission } from '@/types/warung'
import { getSubscriptionStatus } from '@/utils/subscription'

interface NavigationItem { readonly to: string; readonly label: string; readonly icon: AppIconName; readonly group?: string; readonly permission?: WarungPermission; readonly webOnly?: boolean; readonly entitlement?: string; readonly highlight?: boolean }
const route = useRoute()
const router = useRouter()
const store = useWarungStore()
const menuOpen = ref(false)
const navigation: readonly NavigationItem[] = [
  { to: '/app', label: 'Beranda', icon: 'home', group: 'Menu', permission: 'dashboard.view', webOnly: true },
  { to: '/app/produk', label: 'Produk', icon: 'box', group: 'Operasional', permission: 'products.view', webOnly: true },
  { to: '/app/persediaan', label: 'Persediaan', icon: 'database', permission: 'inventory.adjust', webOnly: true },
  { to: '/app/penjualan', label: 'Penjualan', icon: 'cart', permission: 'sales.view', webOnly: true, highlight: true },
  { to: '/app/kas-harian', label: 'Kas Harian', icon: 'wallet', permission: 'cash_sessions.view', webOnly: true },
  { to: '/app/barang-titipan', label: 'Barang Titipan', icon: 'box', permission: 'consignments.view', webOnly: true },
  { to: '/app/pelanggan', label: 'Pelanggan & Bon', icon: 'users', group: 'Relasi', permission: 'customers.view', webOnly: true },
  { to: '/app/supplier', label: 'Supplier', icon: 'truck', permission: 'suppliers.view', webOnly: true },
  { to: '/app/daftar-belanja', label: 'Rencana Belanja', icon: 'cart', permission: 'inventory.adjust', webOnly: true },
  { to: '/app/pembelian', label: 'Pembelian', icon: 'receipt', permission: 'purchases.view', webOnly: true },
  { to: '/app/keuangan', label: 'Uang Usaha', icon: 'wallet', group: 'Analisis', permission: 'finance.view', webOnly: true },
  { to: '/app/laporan', label: 'Laporan', icon: 'chart', permission: 'reports.view', webOnly: true },
  { to: '/app/insight', label: 'Insight Usaha', icon: 'activity', permission: 'reports.view', webOnly: true, entitlement: 'advanced_insights' },
  { to: '/app/audit-log', label: 'Audit Log', icon: 'shield', permission: 'reports.view', webOnly: true, entitlement: 'audit_log' },
  { to: '/app/pengguna', label: 'Pengguna', icon: 'users', group: 'Akun', permission: 'users.view', webOnly: true },
  { to: '/app/perangkat', label: 'Perangkat', icon: 'device', group: 'Akun', permission: 'devices.view' },
  { to: '/app/paket', label: 'Paket', icon: 'user', permission: 'subscription.manage' },
  { to: '/app/akun', label: 'Akun', icon: 'shield', permission: 'account.view' },
]

const visibleNavigation = computed(() =>
  navigation.filter(
    (item) => (item.permission === undefined || store.hasPermission(item.permission))
      && (item.webOnly !== true || store.hasEntitlement('web_access'))
      && (item.entitlement === undefined || store.hasEntitlement(item.entitlement)),
  ),
)

const initials = computed(
  () => store.currentUser?.displayName?.slice(0, 1).toLocaleUpperCase('id-ID') ?? 'W',
)

const roleLabel = computed(() =>
  store.currentUser?.role === 'owner'
    ? 'Pemilik Warung'
    : store.currentUser?.role === 'manager'
      ? 'Pengelola'
      : 'Kasir',
)
async function logout(): Promise<void> { await store.logout(); await router.replace({ name: 'warung-login' }) }

const subscriptionStatus = computed(() => getSubscriptionStatus(store.subscription))

const SIDEBAR_COLLAPSED_KEY = 'gr-sidebar-collapsed'
const sidebarCollapsed = ref(false)
function toggleSidebarCollapsed(): void {
  sidebarCollapsed.value = !sidebarCollapsed.value
  try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed.value ? '1' : '0') } catch { /* penyimpanan lokal tidak tersedia, abaikan */ }
}

const profileMenuOpen = ref(false)
const profileMenuRoot = ref<HTMLElement | null>(null)
const profileMenuLinks = computed(() =>
  visibleNavigation.value.filter((item) => ['/app/pengguna', '/app/perangkat', '/app/paket', '/app/akun'].includes(item.to)))

function toggleProfileMenu(): void { profileMenuOpen.value = !profileMenuOpen.value }
function closeProfileMenu(): void { profileMenuOpen.value = false }
function handleDocumentClick(event: MouseEvent): void {
  if (profileMenuRoot.value && !profileMenuRoot.value.contains(event.target as Node)) closeProfileMenu()
}
function handleKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') closeProfileMenu()
}

watch(() => route.fullPath, () => { menuOpen.value = false; closeProfileMenu() })

onMounted(() => {
  store.refreshSubscriptionIfStale().catch(() => { })
  try { sidebarCollapsed.value = localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1' } catch { /* penyimpanan lokal tidak tersedia, abaikan */ }
  document.addEventListener('click', handleDocumentClick)
  document.addEventListener('keydown', handleKeydown)
})
onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick)
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<template>
  <div class="workspace-shell" :class="{ 'workspace-shell--collapsed': sidebarCollapsed }">
    <button v-if="menuOpen" class="workspace-scrim" type="button" aria-label="Tutup menu" @click="menuOpen = false" />
    <aside class="workspace-sidebar" :class="{ 'workspace-sidebar--open': menuOpen, 'workspace-sidebar--collapsed': sidebarCollapsed }">
      <div class="workspace-brand">
        <img class="workspace-brand__logo" src="/branding/app-icon-48.png" alt="" aria-hidden="true" />
        <div><strong>Galeh Rancak</strong><span>{{ store.currentUser?.warung.name ?? 'Warung' }}</span></div>
      </div>
      <div class="workspace-sidebar-scroll">
        <nav class="workspace-nav" aria-label="Navigasi warung">
          <template v-for="(item, index) in visibleNavigation" :key="item.to">
            <span v-if="item.group && (index === 0 || visibleNavigation[index - 1]?.group !== item.group)"
              class="workspace-nav__group">{{ item.group }}</span>
            <RouterLink :to="item.to" class="workspace-nav__item" :class="{ 'workspace-nav__item--highlight': item.highlight }" exact-active-class="workspace-nav__item--active" :title="item.label">
              <span class="workspace-nav__icon">
                <AppIcon :name="item.icon" :size="18" />
              </span><span>{{ item.label }}</span>
            </RouterLink>
          </template>
        </nav>
        <RouterLink v-if="subscriptionStatus.planCode === 'free' && store.hasPermission('subscription.manage')" to="/app/paket" class="sidebar-upgrade">
          <span class="sidebar-upgrade__icon">
            <AppIcon name="chart" :size="18" />
          </span>
          <strong>Upgrade ke Pro</strong>
          <p>Buka akses Web penuh, tim Pengelola &amp; Kasir, dan laporan lanjutan.</p>
          <span class="sidebar-upgrade__cta">Lihat Paket Pro
            <AppIcon name="arrow-right" :size="13" />
          </span>
        </RouterLink>
      </div>
    </aside>
    <section class="workspace-main">
      <header class="workspace-topbar">
        <button class="workspace-menu-button" type="button" @click="menuOpen = true">
          <AppIcon name="menu" />
        </button>
        <button class="workspace-sidebar-toggle" type="button" :title="sidebarCollapsed ? 'Perluas sidebar' : 'Ciutkan sidebar'" :aria-label="sidebarCollapsed ? 'Perluas sidebar' : 'Ciutkan sidebar'" @click="toggleSidebarCollapsed">
          <AppIcon name="arrow-right" :size="15" />
        </button>
        <div class="workspace-topbar__title"><span>{{ String(route.meta.section ?? 'Beranda') }}</span><strong>{{
          String(route.meta.title ?? 'Beranda') }}</strong></div>
        <div class="workspace-topbar__meta">
          <div v-if="subscriptionStatus.planLabel" class="plan-chip"
            :class="[`plan-chip--${subscriptionStatus.planCode}`, `plan-chip--${subscriptionStatus.urgency}`]">
            <span class="plan-chip__badge">{{ subscriptionStatus.isTrial ? 'PRO · TRIAL' : subscriptionStatus.planLabel
            }}</span>
            <div v-if="subscriptionStatus.planCode === 'pro'" class="plan-chip__period">
              <span v-if="subscriptionStatus.periodDateLabel">{{ subscriptionStatus.isTrial ? 'Trial hingga' :
                subscriptionStatus.inGrace ? 'Masa tenggang hingga' : 'Aktif hingga' }} {{
                subscriptionStatus.periodDateLabel }}</span>
              <span v-else>Aktif tanpa batas waktu</span>
              <small v-if="subscriptionStatus.daysRemainingLabel">{{ subscriptionStatus.daysRemainingLabel }}</small>
            </div>
          </div>
          <div class="workspace-profile-menu" ref="profileMenuRoot">
            <button type="button" class="workspace-profile" aria-haspopup="menu" :aria-expanded="profileMenuOpen"
              @click="toggleProfileMenu">
              <div class="workspace-profile__avatar">{{ initials }}</div>
              <div><strong>{{ store.currentUser?.displayName }}</strong><span>{{ roleLabel }}</span></div>
              <AppIcon class="workspace-profile__chevron" name="chevron-down" :size="15" />
            </button>
            <div v-if="profileMenuOpen" class="workspace-profile-dropdown" role="menu">
              <div class="workspace-profile-dropdown__header">
                <strong>{{ store.currentUser?.warung.name ?? 'Warung' }}</strong>
                <span>{{ store.currentUser?.warung.code }} · {{ store.currentUser?.warung.area || 'Lokasi belum diisi'
                }}</span>
              </div>
              <RouterLink v-for="item in profileMenuLinks" :key="item.to" :to="item.to"
                class="workspace-profile-dropdown__item" role="menuitem" @click="closeProfileMenu">
                <AppIcon :name="item.icon" :size="16" /><span>{{ item.label }}</span>
              </RouterLink>
              <button type="button" class="workspace-profile-dropdown__item workspace-profile-dropdown__item--danger"
                role="menuitem" @click="logout">
                <AppIcon name="arrow-right" :size="16" /><span>Keluar</span>
              </button>
            </div>
          </div>
        </div>
      </header>
      <main class="workspace-page">
        <RouterView />
      </main>
    </section>
  </div>
</template>